import { useState, useEffect } from "react";
import {
  Search,
  Plus,
  Edit,
  Trash2,
  X,
  Check,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { authAPI } from "../../services/api";
import cacheService from "../../services/cacheService";

export default function FarmerSellPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [farmProduce, setFarmProduce] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [isDeletingProduct, setIsDeletingProduct] = useState(false);
  const [toast, setToast] = useState({ show: false, type: '', message: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  const units = [
    "kg", "lb", "oz", "bunch", "each", "dozen", "jar", "basket", "pint", "gallon"
  ];

  // New product form state
  const [newProduct, setNewProduct] = useState({
    name: "",
    category_id: "",
    farm_produce_id: "",
    price: "",
    unit: "kg",
    quantity: "",
    description: "",
    require_verification: false, // Toggle for requiring product verification
  });

  const [formErrors, setFormErrors] = useState({});

  const showToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => {
      setToast({ show: false, type: '', message: '' });
    }, 3000);
  };

  // Fetch categories, products, and farm produce on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [categoriesResult, productsResult, farmProduceResult] = await Promise.all([
          authAPI.getCategories(),
          authAPI.getProducts(),
          authAPI.getMyFarmProduce()
        ]);

        if (categoriesResult) {
          setCategories(categoriesResult);
        }

        if (productsResult.status) {
          setProducts(productsResult.data);
        }

        if (farmProduceResult.status) {
          setFarmProduce(farmProduceResult.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        showToast('error', 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      product.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory =
      categoryFilter === "All" || product.category_name === categoryFilter;

    return matchesSearch && matchesCategory;
  });

  const handleNewProductChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Handle checkbox inputs
    const inputValue = type === 'checkbox' ? checked : value;
    setNewProduct((prev) => ({ ...prev, [name]: inputValue }));

    // Auto-fill product name when farm produce is selected
    if (name === 'farm_produce_id' && value) {
      const selectedProduce = farmProduce.find(p => p.id === parseInt(value));
      if (selectedProduce) {
        setNewProduct(prev => ({ ...prev, name: selectedProduce.name }));
      }
    }

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!newProduct.name.trim()) errors.name = "Product name is required";
    if (!newProduct.category_id) errors.category_id = "Category is required";
    if (!newProduct.farm_produce_id) errors.farm_produce_id = "Farm produce is required";
    if (
      !newProduct.price ||
      isNaN(newProduct.price) ||
      parseFloat(newProduct.price) <= 0
    )
      errors.price = "Valid price is required";
    if (
      !newProduct.quantity ||
      isNaN(newProduct.quantity) ||
      parseInt(newProduct.quantity) <= 0
    )
      errors.quantity = "Valid quantity is required";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setIsAddingProduct(true);

      const productData = {
        category_id: parseInt(newProduct.category_id),
        my_farm_produce_id: parseInt(newProduct.farm_produce_id),
        quantity: parseInt(newProduct.quantity),
        amount: parseFloat(newProduct.price),
        description: newProduct.description,
        unit: newProduct.unit,
        product_name: newProduct.name,
        require_verification: newProduct.require_verification ? 1 : 0 // Send as 0 or 1
      };

      const result = await authAPI.createProduct(productData);

      if (result.status) {
        showToast('success', result.message || 'Product created successfully');

        // Invalidate related cache to force fresh data on dashboard
        await cacheService.forceInvalidateCache([
          '/manage/farm',
          '/my_produce', 
          '/products',
          '/farmer/orders'
        ]);

        // Refresh products list
        const productsResult = await authAPI.getProducts();
        if (productsResult.status) {
          setProducts(productsResult.data);
        }

        // Reset form
        setNewProduct({
          name: "",
          category_id: "",
          farm_produce_id: "",
          price: "",
          unit: "kg",
          quantity: "",
          description: "",
          require_verification: false,
        });
        setShowAddForm(false);
      } else {
        showToast('error', result.message || 'Failed to create product');
      }
    } catch (error) {
      console.error('Error creating product:', error);
      showToast('error', 'Failed to create product');
    } finally {
      setIsAddingProduct(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      setIsDeletingProduct(true);
      const result = await authAPI.deleteProduct(productId);

      if (result.status) {
        showToast('success', 'Product deleted successfully');

        // Invalidate related cache to force fresh data on dashboard
        await cacheService.forceInvalidateCache([
          '/manage/farm',
          '/my_produce', 
          '/products',
          '/farmer/orders'
        ]);

        // Remove from local state
        setProducts(prev => prev.filter(p => p.id !== productId));
      } else {
        showToast('error', result.message || 'Failed to delete product');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      showToast('error', 'Failed to delete product');
    } finally {
      setIsDeletingProduct(false);
      setShowDeleteModal(false);
      setProductToDelete(null);
    }
  };

  const confirmDelete = (product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  if (loading) {
    return (
      <div className="sell-container_ffx">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="sell-container_ffx">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast-notification-fxk ${toast.type === 'success' ? 'toast-success-fxk' : 'toast-error-fxk'}`}>
          <div className="toast-content-fxk">
            <div className="toast-icon-fxk">
              {toast.type === 'success' ? '✓' : '✗'}
            </div>
            <div className="toast-message-fxk">{toast.message}</div>
            <button
              className="toast-close-fxk"
              onClick={() => setToast({ show: false, type: '', message: '' })}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="sell-header_ffx">
        <h2 className="sell-title_ffx">Listed Items</h2>
        <p className="sell-subtitle_ffx">Manage and list your farm products</p>
      </div>

      <div className="sell-search-bar_ffx">
        <div className="search-wrapper_ffx">
          <div className="search-icon_ffx">
            <Search size={18} />
          </div>
          <input
            type="text"
            className="search-input_ffx"
            placeholder="Search products by name or description"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filters_ffx">
          <div className="filter-select-wrapper_ffx">
            <label htmlFor="category-filter" className="filter-label_ffx">
              Category:
            </label>
            <select
              id="category-filter"
              className="filter-select_ffx"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="All">All</option>
              {categories.map((category) => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <button
            className="add-product-button_ffx"
            onClick={() => setShowAddForm(true)}
          >
            <Plus size={16} />
            <span>List New Product</span>
          </button>
        </div>
      </div>

      {/* Product Grid */}
      <div className="products-grid_ffx">
        {filteredProducts.length > 0 ? (
          filteredProducts.map((product) => (
            <div key={product.id} className="product-card_ffx">
              <div className="product-image-container_ffx">
                <img
                  src={product.product_image}
                  alt={product.product_name}
                  className="product-image_ffx"
                />
              </div>
              <div className="product-details_ffx">
                <h3 className="product-name_ffx">{product.product_name}</h3>
                <span className="product-category_ffx">{product.category_name}</span>
                <div className="product-pricing_ffx">
                  <span className="product-price_ffx">
                    ₦{parseFloat(product.amount).toFixed(2)}
                  </span>
                  <span className="product-unit_ffx">/ {product.unit}</span>
                </div>
                <div className="product-inventory_ffx">
                  <span className="inventory-label_ffx">In stock:</span>
                  <span className="inventory-quantity_ffx">
                    {product.quantity} {product.unit}
                  </span>
                </div>
                <p className="product-description_ffx">{product.description}</p>
              </div>

              <div className="product-actions_ffx">
                <Link
                  to={`/farmer-dashboard/product/${product.id}`}
                  className="edit-button_ffx"
                  aria-label="Edit product"
                >
                  <Edit size={16} />
                  <span>Edit</span>
                </Link>
                <button
                  className="delete-button_ffx"
                  onClick={() => confirmDelete(product)}
                  disabled={isDeletingProduct}
                >
                  <Trash2 size={16} />
                  <span>Remove</span>
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="no-products_ffx">
            <p>No products found matching your search criteria.</p>
          </div>
        )}
      </div>

      {/* Add New Product Form Modal */}
      {showAddForm && (
        <div className="modal-overlay_ffx">
          <div className="modal-content_ffx">
            <div className="modal-header_ffx">
              <h3>List New Product</h3>
              <button
                className="close-button_ffx"
                onClick={() => setShowAddForm(false)}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleAddProduct} className="product-form_ffx">
              <div className="form-group_ffx">
                <label htmlFor="farm_produce">Farm Produce</label>
                <select
                  id="farm_produce"
                  name="farm_produce_id"
                  value={newProduct.farm_produce_id}
                  onChange={handleNewProductChange}
                  className={
                    formErrors.farm_produce_id
                      ? "form-select_ffx error_ffx"
                      : "form-select_ffx"
                  }
                >
                  <option value="">Select Farm Produce</option>
                  {farmProduce.map((produce) => (
                    <option key={produce.id} value={produce.id}>
                      {produce.name}
                    </option>
                  ))}
                </select>
                {formErrors.farm_produce_id && (
                  <span className="error-message_ffx">
                    <AlertCircle size={14} />
                    {formErrors.farm_produce_id}
                  </span>
                )}
              </div>
              <div className="form-group_ffx">
                <label htmlFor="name">Product Name</label>
                <input disabled={true}
                  type="text"
                  id="name"
                  name="name"
                  value={newProduct.name}
                  onChange={handleNewProductChange}
                  className={
                    formErrors.name
                      ? "form-input_ffx error_ffx"
                      : "form-input_ffx"
                  }
                />
                {formErrors.name && (
                  <span className="error-message_ffx">
                    <AlertCircle size={14} />
                    {formErrors.name}
                  </span>
                )}
              </div>

              <div className="form-row_ffx">
                <div className="form-group_ffx">
                  <label htmlFor="category">Category</label>
                  <select
                    id="category"
                    name="category_id"
                    value={newProduct.category_id}
                    onChange={handleNewProductChange}
                    className={
                      formErrors.category_id
                        ? "form-select_ffx error_ffx"
                        : "form-select_ffx"
                    }
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {formErrors.category_id && (
                    <span className="error-message_ffx">
                      <AlertCircle size={14} />
                      {formErrors.category_id}
                    </span>
                  )}
                </div>

                <div className="form-group_ffx">
                  <label htmlFor="price">Price (₦)</label>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    min="0.01"
                    step="0.01"
                    value={newProduct.price}
                    onChange={handleNewProductChange}
                    className={
                      formErrors.price
                        ? "form-input_ffx error_ffx"
                        : "form-input_ffx"
                    }
                  />
                  {formErrors.price && (
                    <span className="error-message_ffx">
                      <AlertCircle size={14} />
                      {formErrors.price}
                    </span>
                  )}
                </div>

                <div className="form-group_ffx">
                  <label htmlFor="unit">Unit</label>
                  <select
                    id="unit"
                    name="unit"
                    value={newProduct.unit}
                    onChange={handleNewProductChange}
                    className="form-select_ffx"
                  >
                    {units.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group_ffx">
                <label htmlFor="quantity">Quantity Available</label>
                <input
                  type="number"
                  id="quantity"
                  name="quantity"
                  min="1"
                  value={newProduct.quantity}
                  onChange={handleNewProductChange}
                  className={
                    formErrors.quantity
                      ? "form-input_ffx error_ffx"
                      : "form-input_ffx"
                  }
                />
                {formErrors.quantity && (
                  <span className="error-message_ffx">
                    <AlertCircle size={14} />
                    {formErrors.quantity}
                  </span>
                )}
              </div>

              <div className="form-group_ffx">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description" required
                  rows="3"
                  value={newProduct.description}
                  onChange={handleNewProductChange}
                  className="form-textarea_ffx"
                />
              </div>

              <div className="form-group_ffx">
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px', 
                  cursor: 'pointer',
                  padding: '12px',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <input
                    type="checkbox"
                    name="require_verification"
                    checked={newProduct.require_verification}
                    onChange={handleNewProductChange}
                    style={{ 
                      width: '20px', 
                      height: '20px', 
                      cursor: 'pointer',
                      accentColor: '#0e4410'
                    }}
                  />
                  <div>
                    <span style={{ fontWeight: '500', display: 'block', marginBottom: '4px' }}>
                      Require Product Verification
                    </span>
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>
                      If enabled, your product will be reviewed before being listed. If disabled, it will be approved immediately.
                    </span>
                  </div>
                </label>
              </div>

              <div className="form-actions_ffx">
                <button
                  type="button"
                  className="cancel-button_ffx"
                  onClick={() => setShowAddForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-button_ffx"
                  disabled={isAddingProduct}
                >
                  {isAddingProduct ? (
                    <>
                      <Loader2 size={16} className="loading" /> Creating...
                    </>
                  ) : (
                    <>
                      <Check size={16} /> List Product
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && productToDelete && (
        <div className="modal-overlay_ffx">
          <div className="modal-content_ffx delete-modal">
            <div className="modal-header_ffx">
              <h3>Delete Product</h3>
              <button
                className="close-button_ffx"
                onClick={() => {
                  setShowDeleteModal(false);
                  setProductToDelete(null);
                }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="delete-modal-content">
              <div className="delete-warning">
                <AlertCircle size={48} className="warning-icon" />
                <h4>Are you sure you want to delete this product?</h4>
                <p>This action cannot be undone.</p>
              </div>

              <div className="product-summary">
                <img
                  src={productToDelete.product_image}
                  alt={productToDelete.product_name}
                  className="product-thumbnail"
                />
                <div className="product-info">
                  <h5>{productToDelete.product_name}</h5>
                  <p className="product-category">{productToDelete.category_name}</p>
                  <p className="product-price">₦{parseFloat(productToDelete.amount).toFixed(2)}/{productToDelete.unit}</p>
                </div>
              </div>

              <div className="delete-actions">
                <button
                  className="cancel-button_ffx"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setProductToDelete(null);
                  }}
                  disabled={isDeletingProduct}
                >
                  Cancel
                </button>
                <button
                  className="delete-confirm-btn"
                  onClick={() => handleDeleteProduct(productToDelete.id)}
                  disabled={isDeletingProduct}
                >
                  {isDeletingProduct ? (
                    <>
                      <Loader2 size={16} className="loading" /> Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} /> Delete Product
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .toast-notification-fxk {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 9999;
          max-width: 400px;
          animation: slideInRight 0.3s ease-out;
        }

        .toast-success-fxk {
          background: #10b981;
          color: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .toast-error-fxk {
          background: #ef4444;
          color: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .toast-content-fxk {
          display: flex;
          align-items: center;
          padding: 16px;
          gap: 12px;
        }

        .toast-icon-fxk {
          font-size: 18px;
          font-weight: bold;
          flex-shrink: 0;
        }

        .toast-message-fxk {
          flex: 1;
          font-size: 14px;
          line-height: 1.4;
        }

        .toast-close-fxk {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: background-color 0.2s;
          flex-shrink: 0;
        }

        .toast-close-fxk:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .loading {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .delete-modal {
          max-width: 500px;
        }

        .delete-modal-content {
          padding: 20px 0;
        }

        .delete-warning {
          text-align: center;
          margin-bottom: 24px;
        }

        .warning-icon {
          color: #ef4444;
          margin-bottom: 16px;
        }

        .delete-warning h4 {
          color: #1f2937;
          margin-bottom: 8px;
          font-size: 18px;
        }

        .delete-warning p {
          color: #6b7280;
          font-size: 14px;
        }

        .product-summary {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
          margin-bottom: 24px;
        }

        .product-thumbnail {
          width: 60px;
          height: 60px;
          object-fit: cover;
          border-radius: 8px;
        }

        .product-info h5 {
          margin: 0 0 4px 0;
          color: #1f2937;
          font-size: 16px;
        }

        .product-category {
          margin: 0 0 4px 0;
          color: #6b7280;
          font-size: 14px;
        }

        .product-price {
          margin: 0;
          color: #059669;
          font-weight: 600;
          font-size: 14px;
        }

        .delete-actions {
          display: flex;
          gap: 12px;
          margin: 0px 10px;
          justify-content: flex-end;
        }

        .delete-confirm-btn {
          background: #ef4444;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background-color 0.2s;
        }

        .delete-confirm-btn:hover:not(:disabled) {
          background: #dc2626;
        }

        .delete-confirm-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}
